/**
 * Languages of the app.
 *
 * Messages live in `locales/<code>.js` as flat `key → text` objects; English
 * (`en.js`) is the source and the fallback for any missing key. Texts may
 * hold `{name}` placeholders, and plural forms as `key_one`, `key_other`
 * (… `_few`, `_many` for languages that have them), chosen by `count`.
 *
 * The language comes from, in this order:
 *   1. a choice made on this device with the language button (localStorage,
 *      kept when signed out);
 *   2. the account's language, adopted on this device at sign-in when no
 *      choice was made here (users.locale on the server);
 *   3. the browser's languages;
 *   4. English.
 * At sign-in a device choice is copied to the account, so emails use it too.
 *
 * Static HTML is translated through attributes: `data-i18n` (text),
 * `data-i18n-title`, `data-i18n-placeholder`, `data-i18n-aria-label`.
 * Changing language reloads the page (state is kept, see `setLocale`).
 */
import en from './locales/en.js';

/** Supported languages, with their names as their speakers write them. */
export const LOCALES = [
  { code: 'en', name: 'English' },
  { code: 'fr', name: 'Français' },
  { code: 'es', name: 'Español' },
  { code: 'de', name: 'Deutsch' },
  { code: 'it', name: 'Italiano' },
  { code: 'pt', name: 'Português' },
  { code: 'nl', name: 'Nederlands' },
  { code: 'pl', name: 'Polski' },
  { code: 'ja', name: '日本語' },
  { code: 'zh', name: '中文' },
];
const CODES = LOCALES.map((l) => l.code);

// Explicit loaders (not a glob) so this module also runs under node --test.
const LOADERS = {
  fr: () => import('./locales/fr.js'),
  es: () => import('./locales/es.js'),
  de: () => import('./locales/de.js'),
  it: () => import('./locales/it.js'),
  pt: () => import('./locales/pt.js'),
  nl: () => import('./locales/nl.js'),
  pl: () => import('./locales/pl.js'),
  ja: () => import('./locales/ja.js'),
  zh: () => import('./locales/zh.js'),
};

/** A language picked on this device with the language button. */
export const DEVICE_KEY = 'orbital-locale';
/** The account's language, adopted on this device (no device choice). */
export const ACCOUNT_KEY = 'orbital-locale-account';

let locale = 'en';
let messages = en;
// Numbers and dates: the browser's regional variant of the app language
// when it has one (en-GB, fr-CA, pt-PT…), so an English app in a British
// browser keeps British dates. Chinese stays Simplified (zh-TW would switch
// the script).
let formatLocale = 'en';

function storage() {
  try {
    return globalThis.localStorage ?? null;
  } catch {
    return null;
  }
}
function read(key) {
  try { return storage()?.getItem(key) || null; } catch { return null; }
}
function write(key, value) {
  try { storage()?.setItem(key, value); } catch { /* storage blocked: the choice lasts this page */ }
}

/** The supported language for a code as browsers write it ("fr-BE" → "fr"), or null. */
export function normalizeLocale(code) {
  if (!code) return null;
  const base = String(code).trim().split(/[-_]/)[0].toLowerCase();
  return CODES.includes(base) ? base : null;
}

/**
 * Pick the language (pure, for tests).
 * @param {{device?: string|null, account?: string|null, browser?: string[]}} sources
 */
export function resolveLocale({ device = null, account = null, browser = [] } = {}) {
  for (const code of [device, account, ...browser]) {
    const found = normalizeLocale(code);
    if (found) return found;
  }
  return 'en';
}

function browserLanguages() {
  const nav = globalThis.navigator;
  return nav?.languages?.length ? [...nav.languages] : nav?.language ? [nav.language] : [];
}

/** The browser's regional variant of `code` ("en" → "en-GB"), or `code`. */
export function regionalLocale(code, browser = browserLanguages()) {
  if (code === 'zh') return 'zh-CN';
  for (const tag of browser) {
    if (normalizeLocale(tag) !== code) continue;
    try { return Intl.getCanonicalLocales(tag)[0]; } catch { /* malformed tag: try the next */ }
  }
  return code;
}

/** Load the language chosen for this device and translate the static page. */
export async function initI18n() {
  const chosen = resolveLocale({ device: read(DEVICE_KEY), account: read(ACCOUNT_KEY), browser: browserLanguages() });
  await useLocale(chosen);
  if (globalThis.document) {
    document.documentElement.lang = locale;
    translateDom(document);
  }
  return locale;
}

/** Switch the messages in memory (no reload; tests and init). */
export async function useLocale(code) {
  const next = normalizeLocale(code) || 'en';
  if (next === 'en') {
    messages = en;
  } else {
    try {
      messages = (await LOADERS[next]()).default;
    } catch (err) {
      console.warn(`[i18n] Could not load ${next}, using English:`, err);
      messages = en;
      locale = 'en';
      formatLocale = regionalLocale(locale);
      return locale;
    }
  }
  locale = next;
  formatLocale = regionalLocale(locale);
  return locale;
}

export function getLocale() {
  return locale;
}

/** Whether a language was picked on this device (the button). */
export function hasDeviceChoice() {
  return !!normalizeLocale(read(DEVICE_KEY));
}

function pluralKey(key, count) {
  let rule = 'other';
  try { rule = new Intl.PluralRules(locale).select(count); } catch { /* keep "other" */ }
  return `${key}_${rule}`;
}

/**
 * The text for `key` in the current language, `{placeholders}` filled from
 * `params`. With a numeric `params.count`, the plural form is chosen. Falls
 * back to English, then to the key itself (so a missing text shows up).
 */
export function t(key, params) {
  let text;
  if (params && typeof params.count === 'number') {
    const pk = pluralKey(key, params.count);
    text = messages[pk] ?? messages[`${key}_other`] ?? en[pk] ?? en[`${key}_other`];
  }
  text = text ?? messages[key] ?? en[key];
  if (text === undefined) {
    if (globalThis.localStorage?.getItem?.('orbital-debug') === 'true') console.warn(`[i18n] Missing text: ${key}`);
    return key;
  }
  if (!params) return text;
  return text.replace(/\{(\w+)\}/g, (match, name) => {
    if (!(name in params)) return match;
    const value = params[name];
    return typeof value === 'number' ? formatNumber(value) : String(value);
  });
}

const HTML_ESCAPES = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };

/**
 * t() as HTML: the text and `params` are escaped; `markup` values are trusted
 * HTML put in their placeholders as given (`<kbd>`, `<strong>`…).
 */
export function tHtml(key, params = {}, markup = {}) {
  const slots = {};
  for (const name of Object.keys(markup)) slots[name] = `\u0000${name}\u0000`;
  const text = t(key, { ...params, ...slots }).replace(/[&<>"']/g, (c) => HTML_ESCAPES[c]);
  return text.replace(/\u0000(\w+)\u0000/g, (match, name) => (name in markup ? markup[name] : match));
}

/** Translate the static text of `root`, marked with data-i18n attributes. */
export function translateDom(root = globalThis.document) {
  if (!root?.querySelectorAll) return;
  for (const el of root.querySelectorAll('[data-i18n]')) el.textContent = t(el.dataset.i18n);
  for (const [attr, prop] of [['data-i18n-title', 'title'], ['data-i18n-placeholder', 'placeholder'], ['data-i18n-aria-label', 'aria-label']]) {
    for (const el of root.querySelectorAll(`[${attr}]`)) el.setAttribute(prop, t(el.getAttribute(attr)));
  }
}

export function formatNumber(value, options) {
  try { return new Intl.NumberFormat(formatLocale, options).format(value); } catch { return String(value); }
}

export function formatDate(date, options = { dateStyle: 'medium' }) {
  try { return new Intl.DateTimeFormat(formatLocale, options).format(date instanceof Date ? date : new Date(date)); } catch { return String(date); }
}

/** "3 minutes ago", "in 2 days"… in the current language. */
export function formatRelative(value, unit) {
  try { return new Intl.RelativeTimeFormat(formatLocale, { numeric: 'auto' }).format(value, unit); } catch { return `${value} ${unit}`; }
}

/**
 * Pick a language on this device (the language button): remember it, save it
 * to the account when signed in, then reload the page. The reload goes
 * through `reload` (the app's reloadApp, which first saves pending edits).
 */
export async function setLocale(code, { saveToAccount = null, reload = null } = {}) {
  const next = normalizeLocale(code);
  if (!next) return false;
  write(DEVICE_KEY, next);
  if (saveToAccount) {
    try { await saveToAccount(next); } catch (err) { console.warn('[i18n] Language not saved to the account:', err); }
  }
  if (next === locale) return false;
  if (reload) return reload();
  globalThis.location?.reload();
  return true;
}

/**
 * At sign-in, reconcile this device and the account:
 * - a device choice is copied to the account if it differs;
 * - otherwise the account's language is adopted here (returns "reload" when
 *   the page must reload to show it);
 * - an account without a language gets the current one (emails match the app).
 * @param {string|null} accountLocale  users.locale
 * @param {(code: string) => Promise<unknown>} saveToAccount
 * @returns {Promise<"reload"|null>}
 */
export async function syncWithAccount(accountLocale, saveToAccount) {
  const account = normalizeLocale(accountLocale);
  const device = normalizeLocale(read(DEVICE_KEY));
  const save = async (code) => {
    try { await saveToAccount(code); } catch (err) { console.warn('[i18n] Language not saved to the account:', err); }
  };
  if (device) {
    if (account !== device) await save(device);
    return null;
  }
  if (account) {
    write(ACCOUNT_KEY, account);
    return account !== locale ? 'reload' : null;
  }
  await save(locale);
  return null;
}

/** Tests: reset module state. */
export function _resetForTests() {
  locale = 'en';
  messages = en;
  formatLocale = 'en';
}
